import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import JobDialog from "@/components/portal/JobDialog";
import JobDetailDialog from "@/components/portal/JobDetailDialog";
import DayJobsDialog from "@/components/portal/DayJobsDialog";
import { effectiveTotal } from "@/lib/jobActions";

const DAYS_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAYS_ES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const MONTHS_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

function iso(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default function CalendarPage() {
  const { t, lang } = useLang();
  const [cursor, setCursor] = useState(() => new Date());
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // dialogs
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultDate, setDefaultDate] = useState(iso(new Date()));

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailJob, setDetailJob] = useState(null);

  const [dayOpen, setDayOpen] = useState(false);
  const [dayDate, setDayDate] = useState(null);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const res = await api.get("/jobs");
      setJobs(res.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchJobs(); }, []);

  const days = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startWeekday = first.getDay();
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < startWeekday; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    return arr;
  }, [cursor]);

  const jobsByDate = useMemo(() => {
    const map = {};
    jobs.forEach((j) => {
      if (!map[j.date]) map[j.date] = [];
      map[j.date].push(j);
    });
    return map;
  }, [jobs]);

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => { setCursor(new Date()); };

  const openDay = (dateIso) => { setDayDate(dateIso); setDayOpen(true); };
  const openDetail = (job) => { setDetailJob(job); setDetailOpen(true); };
  const openEdit = (job) => { setEditing(job); setDefaultDate(job?.date || iso(new Date())); setEditOpen(true); };
  const openNew = (dateIso) => { setEditing(null); setDefaultDate(dateIso); setEditOpen(true); };

  const DAYS = lang === "es" ? DAYS_ES : DAYS_EN;
  const MONTHS = lang === "es" ? MONTHS_ES : MONTHS_EN;
  const todayIso = iso(new Date());

  return (
    <div data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
            {MONTHS[cursor.getMonth()]} <span className="text-primary">{cursor.getFullYear()}</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{jobs.length} jobs • {loading ? t.common.loading : "Synced"}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goPrev} className="rounded-full" data-testid="cal-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={goToday} className="rounded-full" data-testid="cal-today-btn">{t.portal.today}</Button>
          <Button variant="outline" size="icon" onClick={goNext} className="rounded-full" data-testid="cal-next-btn"><ChevronRight className="h-4 w-4" /></Button>
          <Button onClick={() => openNew(todayIso)} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" data-testid="cal-add-job-btn">
            <Plus className="h-4 w-4 mr-1" /> {t.portal.addJob}
          </Button>
        </div>
      </div>

      {/* Calendar Grid (responsive) */}
      <div className="mt-6 rounded-xl border border-border overflow-hidden">
        <div className="grid grid-cols-7 bg-card">
          {DAYS.map((d) => (
            <div key={d} className="p-2 md:p-3 text-[10px] md:text-xs uppercase tracking-widest text-muted-foreground text-center border-b border-border">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d, i) => {
            if (!d) return <div key={i} className="min-h-[64px] md:min-h-[110px] border-b border-r border-border bg-background/40" />;
            const key = iso(d);
            const dayJobs = jobsByDate[key] || [];
            const isToday = key === todayIso;
            return (
              <button
                key={i}
                onClick={() => openDay(key)}
                className="text-left min-h-[64px] md:min-h-[110px] p-1.5 md:p-2 border-b border-r border-border transition-colors hover:bg-card/70 focus:outline-none focus:ring-1 focus:ring-primary/40"
                data-testid={`cal-day-${key}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`inline-flex h-6 w-6 md:h-7 md:w-7 items-center justify-center rounded-full text-xs md:text-sm ${isToday ? "bg-primary text-primary-foreground font-bold" : "text-foreground/70"}`}>
                    {d.getDate()}
                  </span>
                  {dayJobs.length > 0 && (
                    <span className="hidden md:inline text-[10px] uppercase tracking-widest text-primary font-bold">{dayJobs.length}</span>
                  )}
                </div>
                {/* Desktop: event chips */}
                <div className="hidden md:block mt-1 space-y-1">
                  {dayJobs.slice(0, 2).map((j) => {
                    const jc = j.status === "cancelled";
                    return (
                    <div
                      key={j.id}
                      onClick={(e) => { e.stopPropagation(); openDetail(j); }}
                      className={`truncate text-[11px] px-1.5 py-0.5 rounded border cursor-pointer transition-colors ${jc ? "bg-muted/40 text-muted-foreground border-border/50 line-through opacity-70" : "bg-primary/20 text-primary border-primary/30 hover:bg-primary/40 hover:border-primary"}`}
                      title={j.client_name}
                      data-testid={`cal-event-${j.id}`}
                    >
                      {j.start_time} {j.client_name}
                    </div>
                    );
                  })}
                  {dayJobs.length > 2 && <div className="text-[10px] text-muted-foreground">+{dayJobs.length - 2} more</div>}
                </div>
                {/* Mobile: dots indicator */}
                <div className="md:hidden mt-1 flex justify-center gap-0.5">
                  {dayJobs.slice(0, 3).map((_, idx) => (
                    <span key={idx} className="h-1.5 w-1.5 rounded-full bg-primary" />
                  ))}
                  {dayJobs.length > 3 && (
                    <span className="text-[8px] text-primary font-bold ml-0.5">+{dayJobs.length - 3}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobile: upcoming next 5 */}
      <div className="mt-6 md:hidden rounded-xl border border-border bg-card p-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
          {lang === "es" ? "Próximos trabajos" : "Upcoming jobs"}
        </div>
        {jobs.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">{t.portal.noJobs}</div>}
        <div className="space-y-2">
          {jobs
            .filter((j) => j.date >= todayIso)
            .slice(0, 5)
            .map((j) => {
              const jc = j.status === "cancelled";
              return (
              <button
                key={j.id}
                onClick={() => openDetail(j)}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${jc ? "border-border/50 opacity-60 hover:border-border" : "border-border hover:border-primary"}`}
                data-testid={`mobile-upcoming-${j.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className={`text-[10px] uppercase font-bold ${jc ? "text-muted-foreground" : "text-primary"}`}>{j.date} · {j.start_time}</div>
                    <div className={`font-semibold truncate ${jc ? "line-through" : ""}`}>{j.client_name}</div>
                    <div className={`text-xs text-muted-foreground truncate ${jc ? "line-through" : ""}`}>{j.service_type}</div>
                  </div>
                  <div className={`font-bold ${jc ? "line-through text-muted-foreground" : "text-primary"}`}>${effectiveTotal(j).toFixed(0)}</div>
                </div>
              </button>
              );
            })}
        </div>
      </div>

      <JobDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        job={detailJob}
        onEdit={openEdit}
        onChanged={fetchJobs}
      />

      <DayJobsDialog
        open={dayOpen}
        onOpenChange={setDayOpen}
        date={dayDate}
        jobs={dayDate ? (jobsByDate[dayDate] || []) : []}
        onSelectJob={openDetail}
        onAddJob={openNew}
      />

      <JobDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        job={editing}
        defaultDate={defaultDate}
        onSaved={fetchJobs}
      />
    </div>
  );
}
